import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAppContext } from "../../AppContext";
import { useTranslation } from "react-i18next";
import { MyDomain } from "../../App";

export default function MPHomeShowProducts() {
  const { AllData, currencySymbol } = useAppContext();
  const { t } = useTranslation();

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    if (AllData?.barberData?.phoneNumber) {
      fetchProducts(AllData.barberData.phoneNumber);
    }
  }, [AllData]);

  const fetchProducts = async (phoneNumber) => {
    try {
      const response = await axios.post(
        `${MyDomain}Products/GetAllBarberProducts`,
        { BarberPhoneNumber: phoneNumber },
      );
      setProducts(response.data);
    } catch (err) {
      console.log("Failed to fetch products:", err.message);
    }
  };

  const categories = [
    "All",
    ...new Set(
      products.map((p) => p.category).filter((c) => c && c.trim() !== ""),
    ),
  ];

  const filtered =
    selectedCategory === "All"
      ? products
      : products.filter((p) => p.category === selectedCategory);

  if (products.length === 0) return null;

  const hasDiscount = (p) =>
    p.priceAfterDiscount &&
    p.priceAfterDiscount > 0 &&
    p.priceAfterDiscount < p.price;

  const discountPercent = (p) =>
    Math.round(((p.price - p.priceAfterDiscount) / p.price) * 100);

  const openModal = (product) => {
    setSelectedProduct(product);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setSelectedProduct(null);
  };

  return (
    <div style={styles.container}>
      {/* Section Header */}
      <div style={styles.sectionHeader}>
        <div>
          <div style={styles.sectionTitle}>{t("products") || "Products"}</div>
          <div style={styles.sectionSubtitle}>
            {products.length} {t("itemsAvailable") || "items available"}
          </div>
        </div>
        <div style={styles.sectionIcon}>
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          </svg>
        </div>
      </div>

      {/* Category Filter */}
      {categories.length > 1 && (
        <div style={styles.categoryScroll}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              style={{
                ...styles.categoryChip,
                ...(selectedCategory === cat ? styles.categoryChipActive : {}),
              }}
            >
              <span
                style={{
                  ...styles.categoryChipText,
                  ...(selectedCategory === cat
                    ? styles.categoryChipTextActive
                    : {}),
                }}
              >
                {cat === "All" ? t("all") || "All" : cat}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Products Grid */}
      <div style={styles.grid}>
        {filtered.map((item) => (
          <div
            key={item.id}
            style={styles.card}
            onClick={() => openModal(item)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)";
            }}
          >
            {/* Image */}
            <div style={styles.cardImageWrapper}>
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.name}
                  style={styles.cardImage}
                />
              ) : (
                <div style={styles.cardImagePlaceholder}>
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#6366f1"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  </svg>
                </div>
              )}

              {hasDiscount(item) && (
                <div style={styles.discountBadge}>
                  <span style={styles.discountBadgeText}>
                    -{discountPercent(item)}%
                  </span>
                </div>
              )}
            </div>

            {/* Info */}
            <div style={styles.cardInfo}>
              <div style={styles.cardName}>{item.name}</div>

              {item.category && (
                <div style={styles.cardCategoryRow}>
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#6366f1"
                    strokeWidth="2"
                  >
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                    <line x1="7" y1="7" x2="7.01" y2="7" />
                  </svg>
                  <span style={styles.cardCategory}>{item.category}</span>
                </div>
              )}

              <div style={styles.cardPriceRow}>
                {hasDiscount(item) ? (
                  <>
                    <span style={styles.cardDiscountPrice}>
                      {item.priceAfterDiscount} {currencySymbol}
                    </span>
                    <span style={styles.cardOriginalPrice}>
                      {item.price} {currencySymbol}
                    </span>
                  </>
                ) : (
                  <span style={styles.cardPrice}>
                    {item.price} {currencySymbol}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Product Detail Modal */}
      {modalVisible && selectedProduct && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            {/* Drag indicator */}
            <div style={styles.dragIndicator} />

            {/* Image */}
            <div style={styles.modalImageContainer}>
              {selectedProduct.image ? (
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  style={styles.modalImage}
                />
              ) : (
                <div style={styles.modalImagePlaceholder}>
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#6366f1"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  </svg>
                </div>
              )}

              {hasDiscount(selectedProduct) && (
                <div style={styles.modalDiscountBadge}>
                  <span style={styles.modalDiscountBadgeText}>
                    -{discountPercent(selectedProduct)}% OFF
                  </span>
                </div>
              )}

              {selectedProduct.category && (
                <div style={styles.modalCategoryBadge}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                    <line x1="7" y1="7" x2="7.01" y2="7" />
                  </svg>
                  <span style={styles.modalCategoryBadgeText}>
                    {selectedProduct.category}
                  </span>
                </div>
              )}
            </div>

            {/* Scrollable Content */}
            <div style={styles.modalContent}>
              <div style={styles.modalName}>{selectedProduct.name}</div>

              {/* Price Card */}
              <div style={styles.modalPriceCard}>
                {hasDiscount(selectedProduct) ? (
                  <div>
                    <div style={styles.modalPriceRow}>
                      <span style={styles.modalDiscountPrice}>
                        {selectedProduct.priceAfterDiscount} {currencySymbol}
                      </span>
                      <span style={styles.modalOriginalPrice}>
                        {selectedProduct.price} {currencySymbol}
                      </span>
                    </div>
                    <div style={styles.savingsBadge}>
                      <span style={styles.savingsBadgeText}>
                        {t("youSave") || "You save"}{" "}
                        {selectedProduct.price -
                          selectedProduct.priceAfterDiscount}{" "}
                        {currencySymbol}
                      </span>
                    </div>
                  </div>
                ) : (
                  <span style={styles.modalPrice}>
                    {selectedProduct.price} {currencySymbol}
                  </span>
                )}
              </div>

              {/* Description */}
              {selectedProduct.disc && selectedProduct.disc.trim() !== "" && (
                <div style={styles.modalDescCard}>
                  <div style={styles.modalDescLabel}>
                    {t("description") || "Description"}
                  </div>
                  <div style={styles.modalDesc}>{selectedProduct.disc}</div>
                </div>
              )}
            </div>

            {/* Close Button */}
            <div style={styles.modalCloseBtnWrapper}>
              <button style={styles.modalCloseBtn} onClick={closeModal}>
                {t("close") || "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    paddingTop: "24px",
    paddingBottom: "8px",
    borderTop: "1px solid #f1f5f9",
  },

  // Header
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: "16px",
    paddingRight: "16px",
    marginBottom: "16px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1a1a1a",
  },
  sectionSubtitle: {
    fontSize: "13px",
    color: "#6b7280",
    marginTop: "2px",
  },
  sectionIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },

  // Category Filter
  categoryScroll: {
    display: "flex",
    gap: "8px",
    overflowX: "auto",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: "14px",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  categoryChip: {
    padding: "7px 16px",
    borderRadius: "20px",
    border: "1.5px solid #e5e7eb",
    backgroundColor: "#fff",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
    transition: "all 0.15s ease",
  },
  categoryChipActive: {
    borderColor: "#6366f1",
    backgroundColor: "#6366f1",
  },
  categoryChipText: {
    fontSize: "13px",
    fontWeight: "500",
    color: "#6b7280",
  },
  categoryChipTextActive: {
    color: "#fff",
    fontWeight: "700",
  },

  // Grid
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: "12px",
    paddingLeft: "16px",
    paddingRight: "16px",
  },

  // Card
  card: {
    backgroundColor: "#fff",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    overflow: "hidden",
    cursor: "pointer",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  },
  cardImageWrapper: {
    position: "relative",
    width: "100%",
    aspectRatio: "1",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  cardImagePlaceholder: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  discountBadge: {
    position: "absolute",
    top: "8px",
    left: "8px",
    backgroundColor: "#ef4444",
    padding: "3px 7px",
    borderRadius: "6px",
  },
  discountBadgeText: {
    color: "#fff",
    fontSize: "11px",
    fontWeight: "700",
  },
  cardInfo: {
    padding: "10px",
  },
  cardName: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#111827",
    marginBottom: "4px",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardCategoryRow: {
    display: "flex",
    alignItems: "center",
    gap: "3px",
    marginBottom: "6px",
  },
  cardCategory: {
    fontSize: "11px",
    color: "#6366f1",
    fontWeight: "600",
  },
  cardPriceRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
  },
  cardPrice: {
    fontSize: "15px",
    fontWeight: "800",
    color: "#6366f1",
  },
  cardDiscountPrice: {
    fontSize: "15px",
    fontWeight: "800",
    color: "#10b981",
  },
  cardOriginalPrice: {
    fontSize: "12px",
    color: "#9ca3af",
    textDecoration: "line-through",
  },

  // Modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    zIndex: 9999,
  },
  modalSheet: {
    backgroundColor: "#fff",
    borderRadius: "24px 24px 0 0",
    width: "100%",
    maxWidth: "520px",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  dragIndicator: {
    width: "40px",
    height: "4px",
    backgroundColor: "#e5e7eb",
    borderRadius: "2px",
    margin: "12px auto 0",
    flexShrink: 0,
  },
  modalImageContainer: {
    width: "100%",
    height: "240px",
    position: "relative",
    flexShrink: 0,
  },
  modalImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  modalImagePlaceholder: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalDiscountBadge: {
    position: "absolute",
    top: "12px",
    left: "12px",
    backgroundColor: "#ef4444",
    padding: "5px 12px",
    borderRadius: "8px",
  },
  modalDiscountBadgeText: {
    color: "#fff",
    fontSize: "13px",
    fontWeight: "700",
  },
  modalCategoryBadge: {
    position: "absolute",
    top: "12px",
    right: "12px",
    backgroundColor: "rgba(99,102,241,0.9)",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "5px 10px",
    borderRadius: "20px",
  },
  modalCategoryBadgeText: {
    color: "#fff",
    fontSize: "12px",
    fontWeight: "600",
  },
  modalContent: {
    padding: "20px",
    overflowY: "auto",
    flex: 1,
  },
  modalName: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#111827",
    marginBottom: "16px",
  },
  modalPriceCard: {
    backgroundColor: "#f5f3ff",
    borderRadius: "14px",
    padding: "16px",
    marginBottom: "16px",
  },
  modalPriceRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  modalPrice: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#6366f1",
  },
  modalDiscountPrice: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#10b981",
  },
  modalOriginalPrice: {
    fontSize: "18px",
    color: "#9ca3af",
    textDecoration: "line-through",
  },
  savingsBadge: {
    display: "inline-block",
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    padding: "4px 10px",
    borderRadius: "20px",
    marginTop: "8px",
  },
  savingsBadgeText: {
    color: "#fff",
    fontSize: "12px",
    fontWeight: "700",
  },
  modalDescCard: {
    backgroundColor: "#f9fafb",
    borderRadius: "0 12px 12px 0",
    borderLeft: "3px solid #6366f1",
    padding: "14px",
  },
  modalDescLabel: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "6px",
  },
  modalDesc: {
    fontSize: "14px",
    color: "#374151",
    lineHeight: "1.6",
  },
  modalCloseBtnWrapper: {
    padding: "0 20px 30px",
    flexShrink: 0,
  },
  modalCloseBtn: {
    width: "100%",
    padding: "15px",
    borderRadius: "12px",
    backgroundColor: "#f3f4f6",
    border: "none",
    fontSize: "16px",
    fontWeight: "600",
    color: "#6b7280",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
  },
};
